import { useState, useEffect } from 'react';
import QRCode from 'qrcode';
import { Copy, Download, Check } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useToast } from '../hooks/use-toast';

/**
 * Reusable share dialog. Wrap any trigger element as `children`.
 *
 * Usage:
 *   <ShareDialog book={book}>
 *     <Button variant="ghost" size="icon"><Share2 /></Button>
 *   </ShareDialog>
 *
 * Anyone who opens the share URL while signed in will have the book added
 * to their library. Unauthenticated visitors get sent to login first, then
 * routed back to the add flow (handled by AddToLibrary.jsx + Login.jsx).
 */
export function ShareDialog({ book, children }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [copied, setCopied] = useState(false);

  // Build the shareable URL using the current origin so it works in dev + prod
  const shareUrl = `${window.location.origin}/add/${book.id}`;

  // Generate the QR code only when the dialog opens (avoids unnecessary work)
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    QRCode.toDataURL(shareUrl, {
      width: 512,
      margin: 2,
      color: { dark: '#000000', light: '#ffffff' },
      errorCorrectionLevel: 'M',
    })
      .then((url) => {
        if (!cancelled) setQrDataUrl(url);
      })
      .catch((err) => {
        console.error('[ShareDialog] QR generation failed:', err);
      });
    return () => {
      cancelled = true;
    };
  }, [open, shareUrl]);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast({ title: 'Link copied', description: 'Share it anywhere.' });
      // Revert the visual state after a moment
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({
        variant: 'destructive',
        title: 'Copy failed',
        description: 'Your browser blocked clipboard access.',
      });
    }
  };

  const handleDownloadQR = () => {
    if (!qrDataUrl) return;
    const slug = book.title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
    const link = document.createElement('a');
    link.download = `readwell-${slug}-qr.png`;
    link.href = qrDataUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) setCopied(false);
      }}
    >
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl">Share "{book.title}"</DialogTitle>
          <DialogDescription>
            Anyone with this link can add the book to their library. Share it as a link,
            or download a QR code to print or stick on a syllabus.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-2">
          {/* Link section */}
          <div className="space-y-2">
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Shareable link
            </label>
            <div className="flex items-center gap-2">
              <Input
                value={shareUrl}
                readOnly
                onFocus={(e) => e.target.select()}
                className="font-mono text-sm"
              />
              <Button
                onClick={handleCopyLink}
                variant="outline"
                size="icon"
                aria-label="Copy link"
              >
                {copied ? (
                  <Check className="h-4 w-4 text-green-600" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>

          {/* QR section */}
          <div className="space-y-3">
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              QR code
            </label>
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="bg-white border border-slate-200 rounded-lg p-3 flex-shrink-0">
                {qrDataUrl ? (
                  <img
                    src={qrDataUrl}
                    alt={`QR code for ${book.title}`}
                    className="w-40 h-40"
                  />
                ) : (
                  <div className="w-40 h-40 flex items-center justify-center text-xs text-muted-foreground">
                    Generating…
                  </div>
                )}
              </div>
              <div className="flex-1 space-y-3">
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Print this code or stick it on your syllabus. Students who scan it will
                  be taken to the book — and signed-in users will have it added to their
                  library automatically.
                </p>
                <Button
                  onClick={handleDownloadQR}
                  disabled={!qrDataUrl}
                  className="w-full sm:w-auto"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Download PNG
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
